import React, { useEffect, useMemo, useState } from 'react';
import { parseApiDate } from '../utils/security';
import {
  fetchTsKayitGecerlilikSecenekleri,
  updateTsKayitDavetGecerlilik,
} from '../services/tsKayitBasvuruService';
import './TsKayitDavetGecerlilikModal.css';

const formatTarih = (val) => {
  const d = val instanceof Date ? val : parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const computeOnizlemeBitis = (createdAt, sureMs) => {
  const base = parseApiDate(createdAt);
  if (!base || Number.isNaN(base.getTime()) || !sureMs) return null;
  return new Date(base.getTime() + sureMs);
};

const TsKayitDavetGecerlilikModal = ({ davet, onClose, onSaved }) => {
  const [secenekler, setSecenekler] = useState([]);
  const [varsayilanSureMs, setVarsayilanSureMs] = useState(null);
  const [sureMs, setSureMs] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetchTsKayitGecerlilikSecenekleri();
        if (cancelled) return;
        const list = res?.veri?.secenekler || [];
        setSecenekler(list);
        setVarsayilanSureMs(res?.veri?.varsayilanSureMs ?? null);
        if (list.length > 0) {
          setSureMs(String(list.find((s) => s.sureMs === res?.veri?.varsayilanSureMs)?.sureMs ?? list[0].sureMs));
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Seçenekler yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const seciliMs = parseInt(sureMs, 10);
  const seciliLabel = secenekler.find((s) => s.sureMs === seciliMs)?.label || '';
  const onizlemeBitis = useMemo(
    () => computeOnizlemeBitis(davet?.createdAt, seciliMs),
    [davet?.createdAt, seciliMs],
  );
  const gecmisOnizleme = onizlemeBitis && onizlemeBitis.getTime() <= Date.now();

  const handleSave = async () => {
    if (!davet?.id || !Number.isFinite(seciliMs)) return;
    setSaving(true);
    setError('');
    try {
      const res = await updateTsKayitDavetGecerlilik(davet.id, seciliMs);
      if (res?.basarili === false) {
        setError(res.mesaj || 'Kaydedilemedi');
        return;
      }
      window.showNotification?.('success', 'Geçerlilik süresi güncellendi');
      onSaved?.(res?.veri || null);
      onClose?.();
    } catch (err) {
      setError(err?.message || err?.data?.mesaj || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay ts-gecerlilik-modal" onClick={onClose} role="presentation">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>
            <i className="fas fa-clock" />
            Geçerlilik süresi
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="ts-gecerlilik-loading">
              <div className="loading-spinner" />
              <span>Yükleniyor</span>
            </div>
          ) : (
            <>
              <div className="ts-gecerlilik-info">
                <div className="ts-gecerlilik-info-item">
                  <span>Oluşturma</span>
                  <strong>{formatTarih(davet?.createdAt)}</strong>
                </div>
                <div className="ts-gecerlilik-info-item">
                  <span>Mevcut bitiş</span>
                  <strong>{formatTarih(davet?.gecerlilikBitis)}</strong>
                </div>
              </div>

              <div className="ts-gecerlilik-field">
                <label htmlFor="ts-gecerlilik-sure">Süre (oluşturulma anından)</label>
                <select
                  id="ts-gecerlilik-sure"
                  value={sureMs}
                  onChange={(e) => setSureMs(e.target.value)}
                  disabled={saving}
                >
                  {secenekler.map((s) => (
                    <option key={s.sureMs} value={String(s.sureMs)}>
                      {s.label}
                      {s.sureMs === varsayilanSureMs ? ' (varsayılan)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {onizlemeBitis && (
                <div className={`ts-gecerlilik-preview${gecmisOnizleme ? ' is-invalid' : ''}`}>
                  <div className="ts-gecerlilik-preview-label">Yeni bitiş</div>
                  <div className="ts-gecerlilik-preview-value">{formatTarih(onizlemeBitis)}</div>
                  {seciliLabel && (
                    <div className="ts-gecerlilik-preview-hint">{seciliLabel} · oluşturulma + süre</div>
                  )}
                </div>
              )}

              {gecmisOnizleme && (
                <div className="ts-gecerlilik-alert is-error">
                  Seçilen süre geçmişte kalıyor; daha uzun bir süre seçin.
                </div>
              )}

              {error && (
                <div className="ts-gecerlilik-alert is-error">{error}</div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose} disabled={saving}>
            İptal
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleSave}
            disabled={loading || saving || gecmisOnizleme || !Number.isFinite(seciliMs)}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TsKayitDavetGecerlilikModal;
